<template>
	<core-layout-content large>
		<core-layout-headline-and-actions>
			<template slot="headline">
				<div class="flex xs-middle">
					<!-- Back button -->
					<j-button
						size="small"
						type="secondary"
						@click="$router.push({ name: 'TemplatesList' })"
						class="mr2"
					>
						<crm-icon name="back" size="normal" color="c5" class="mr1" />
					</j-button>
					{{ $t("notification_template") }}
				</div>
			</template>
			<template slot="actions">
				<!-- Preview -->
				<j-button size="small" :isLoading="isLoading" @click="openPreviewModal">
					{{ $t("preview") }}
				</j-button>
			</template>
		</core-layout-headline-and-actions>

		<j-loading-toast v-if="isLoading" />
		<div v-if="template">
			<!-- Name and description -->
			<j-card class="xs-12 bg5 p4 mb6">
				<core-form-input
					class="mb4"
					componentName="j-base-input-text"
					name="name"
					id="name"
					:label="$t('name_of_template')"
					:validationErrors="errors"
					:value="template.name"
					@input="onFieldInput('name', $event)"
					v-validate="{ required: true }"
				/>
				<core-form-input
					class="mb4"
					componentName="j-base-input-long-text"
					name="description"
					id="description"
					:label="$t('description')"
					:value="template.description"
					@input="onFieldInput('description', $event)"
				/>
			</j-card>

			<!-- Content -->
			<j-card class="bg5 p4">
				<div class="pr2 xs-12 lh125 fw7 pb2">{{ $t("content") }}</div>
				<notification-template-html
					:id="template.id"
					:content="computedHtmlPayload"
					:selectedLocale="selectedLocale"
					@fetch="fetch"
					@isLoading="isLoading = $event"
					@selectedLocale="selectedLocale = $event"
				/>
			</j-card>

			<!-- Bottom Actions -->
			<div class="w-full flex justify-end">
				<div class="flex mt6">
					<!-- Delete -->
					<j-button type="error" size="small" @click="$modal.show('j-dialog')">
						<crm-icon name="delete" size="normal" color="c5" class="mr1" />
						{{ $t("delete_template") }}
					</j-button>
				</div>
			</div>
		</div>

		<!-- Delete Modal/Dialog -->
		<j-dialog
			:title="$t('delete_template')"
			type="delete"
			@confirm="deleteTemplate"
			>{{ $t("errors.are_you_sure_you_want_to_delete_template") }}</j-dialog
		>
		<!-- preview modal -->
		<core-modal name="preview_template" :headline="$t('preview')">
			<notification-template-preview :html="computedHtmlPayload" />
		</core-modal>
	</core-layout-content>
</template>

<script>
import CrmIcon from "@/components/CrmIcon"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent.vue"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions.vue"
import CoreModal from "@/modules/core/components/CoreModal.vue"
import CoreFormInput from "@/modules/core/components/CoreFormInput"
import NotificationTemplateHtml from "../components/NotificationTemplate/NotificationTemplateHtml"
import NotificationTemplatePreview from "../components/NotificationTemplate/NotificationTemplatePreview"
import JButton from "@/components/JButton"
import JLoadingToast from "@/components/JLoadingToast"
import JCard from "@/components/JCard"
import JDialog from "@/components/JDialog/"
import debounce from "lodash.debounce"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import { i18n } from "@/locales/setup.js"

export default {
	components: {
		CrmIcon,
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		CoreModal,
		NotificationTemplateHtml,
		NotificationTemplatePreview,
		JButton,
		JLoadingToast,
		JCard,
		JDialog,
		CoreFormInput
	},
	props: {
		id: {
			type: String
		}
	},

	data: () => ({
		source: null,
		isLoading: false,
		updating: false,
		error: null,
		template: null,
		selectedLocale: i18n.locale
	}),
	computed: {
		computedHtmlPayload() {
			return this.template?.localizedHtml || " "
		}
	},
	methods: {
		onFieldInput: debounce(function(fieldName, value) {
			this.updateTemplate(fieldName, value)
		}, 500),

		async openPreviewModal() {
			// Need to refetch first, to get latest version of html
			await this.fetch()
			this.$modal.show("preview_template")
		},

		async updateTemplate(fieldName, value) {
			const query = gql`
				mutation updateNotificationTemplate(
					$templateId: String!
					$input: updateNotificationTemplateInput!
				) {
					updateNotificationTemplate(templateId: $templateId, input: $input) {
						status
						errors
					}
				}
			`

			const variables = {
				templateId: this.id,
				input: {
					[fieldName]: value
				}
			}

			this.isLoading = true

			const res = await api({ query, variables })

			this.isLoading = false

			if (res?.data?.updateNotificationTemplate?.status === "success") {
				this.fetch()
			} else {
				this.$store.dispatch(
					"showError",
					res?.data?.errors ||
						res?.data?.updateNotificationTemplate?.errors || [
							this.$t("errors.general")
						]
				)
			}
		},

		navigateTo() {
			this.$router.push({ name: "TemplatesList" })
		},

		async fetch() {
			this.isLoading = true
			const query = gql`
				query templates($where: templateWhereInput) {
					templates(where: $where) {
						list {
							id
							name
							description
							__typename
							... on notificationTemplate {
								localizedHtml
							}
						}
					}
				}
			`
			const variables = {
				where: {
					id: this.id
				}
			}
			const res = await api({ query, variables, locale: this.selectedLocale })
			this.isLoading = false
			this.template = res?.data?.templates?.list?.[0]
		},

		async deleteTemplate() {
			/* Delete l10n */
			const deleteAllL10n = gql`
				mutation deleteAllL10n($key: String!) {
					deleteAllL10n(key: $key) {
						errors
						status
					}
				}
			`
			const variables = {
				key: `templates-${this.id}-html`
			}
			const l10nRes = await api({ query: deleteAllL10n, variables })
			if (l10nRes.errors) this.$store.dispatch("showError", l10nRes.errors)

			/* delete template */
			const query = gql`
				mutation deleteTemplate($templateId: String!) {
					deleteTemplate(templateId: $templateId) {
						status
						errors
					}
				}
			`
			const res = await api({
				query: query,
				variables: { templateId: this.id }
			})
			if (res.errors) this.$store.dispatch("showError", res.errors)
			this.$router.push({ name: "TemplatesList" })
		}
	},

	created() {
		this.fetch()
	}
}
</script>
